// Pure cache-shape walking for the optimistic vote UI (issue #241).
// Extracted from hooks/use-vote.ts's onMutate so the shape-sniffing it
// used to do inline is unit-tested with zero mocks -- this is the one
// place that knows how a vote target sits inside each cached shape:
// the threads list is an infinite-query {pages} object, thread-detail's
// replies are a bare array, and the thread itself is a bare object.
import { applyVoteChange, applyVoteChangeToPages, type VoteDirection } from '@/lib/vote-math';

type Votable = { id: string; score: number; viewerVote?: 'up' | 'down' };

function isMatchingVotable(item: unknown, id: string): item is Votable {
  return item !== null && typeof item === 'object' && 'id' in item && (item as Votable).id === id && 'score' in item;
}

// Patches the matching vote target in whatever shape it is cached under,
// immutably, returning the input by reference when nothing matches:
// an infinite-query {pages} object (each page carries `threads` or
// `items`), a bare array of items, or a single object.
export function patchVotable(data: unknown, id: string, direction: VoteDirection | undefined): unknown {
  if (data === null || data === undefined) return data;

  if (Array.isArray(data)) {
    return data.map((item) => (isMatchingVotable(item, id) ? applyVoteChange(item, direction) : item));
  }

  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    if (Array.isArray(obj.pages)) {
      return { ...obj, pages: applyVoteChangeToPages(obj.pages, id, direction) };
    }

    if (isMatchingVotable(obj, id)) {
      return applyVoteChange(obj, direction);
    }
  }

  return data;
}
